import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Col, Form, ProgressBar, Row, Spinner } from "react-bootstrap";
import { connect } from "react-redux";

import {
    addProductManager,
    changeProductManagerRole,
    inviteProductManager,
    lifecycle,
    productManagement,
    removeProductManager,
    repairLifecycle,
    resendProductManagerInvitation,
    retryLifecycle,
    revokeProductManagerInvitation,
    saveLifecycleSettings,
    searchProductManagers,
    tokenizeProduct,
    updateInternalTag
} from "../../../actions/products";
import { ProductIdentityHeader, ProductManagementDrawer, ProductSummaryBand } from "./components";
import { CustomDomainsSection } from "./custom-domains";
import { customDomainsUiEnabled } from "../../../utils/customDomainApi";
import { deepEqual } from "../../../utils/object";
import "./workspace.css";

const TIME_ZONES = [
    "America/New_York",
    "America/Chicago",
    "America/Denver",
    "America/Los_Angeles",
    "America/Phoenix",
    "Pacific/Honolulu"
];

export const statusPresentation = (data, saving) => {
    if (saving) return { label: "Saving", variant: "info" };
    if (data?.instance?.status === "Suspended") return { label: "Suspended", variant: "warning" };
    if (data?.operation?.status === "Failed") return { label: "Failed", variant: "danger" };
    if (data?.operation?.status === "Pending") return { label: "Pending", variant: "secondary" };
    if (data?.operation?.status === "AwaitingProduct") return { label: "Awaiting acknowledgment", variant: "info" };
    if (["Published", "Ready"].includes(data?.settings?.status)) return { label: data.settings.status === "Ready" ? "Provisioning" : "Syncing", variant: "info" };
    if (data?.settings?.status === "Applied" || (data?.instance?.status === "Active" && data?.operation?.status === "Completed")) return { label: "Ready", variant: "success" };
    return { label: "Draft", variant: "secondary" };
};

export const matchesSettingRule = (rule, values = {}) => {
    if (!rule) return true;
    if (Object.prototype.hasOwnProperty.call(rule, "equals")) return values[rule.field] === rule.equals;
    if (Object.prototype.hasOwnProperty.call(rule, "notEquals")) return values[rule.field] !== rule.notEquals;
    return true;
};

export const changedSettingValues = (baseline = {}, current = {}) => Object.fromEntries(
    Object.keys(current)
        .filter(key => !deepEqual(baseline[key], current[key]))
        .map(key => [key, current[key]])
);

export const SettingControl = ({ field, value, error, onChange, disabled }) => {
    const inputId = `product-setting-${field.key}`;
    const options = field.control === "timeZone"
        ? TIME_ZONES.map(item => ({ value: item, label: item.replaceAll("_", " ") }))
        : (field.options || []);

    if (["radioCards", "templatePicker", "roleMatrix"].includes(field.control)) {
        return (
            <fieldset className="product-setting-field" disabled={disabled}>
                <legend>{field.label}{field.required ? <span aria-hidden="true"> *</span> : null}</legend>
                {field.description ? <p>{field.description}</p> : null}
                <div className="product-setting-choices">
                    {options.map(option => (
                        <button
                            type="button"
                            className={`product-setting-choice ${value === option.value ? "is-selected" : ""}`}
                            key={option.value}
                            onClick={() => onChange(option.value)}
                            aria-pressed={value === option.value}
                        >
                            <span className="product-setting-choice-mark" aria-hidden="true">{value === option.value ? "✓" : ""}</span>
                            <strong>{option.label}</strong>
                            {option.description ? <small>{option.description}</small> : null}
                            {field.recommendedValue === option.value ? <Badge bg="primary">Recommended</Badge> : null}
                        </button>
                    ))}
                </div>
                {error ? <div className="invalid-feedback d-block">{error}</div> : null}
            </fieldset>
        );
    }

    if (field.control === "toggle") {
        return (
            <div className="product-setting-toggle-card">
                <div>
                    <Form.Label htmlFor={inputId}>{field.label}</Form.Label>
                    {field.description ? <p>{field.description}</p> : null}
                    {field.recommendedValue === value ? <Badge bg="light" text="primary">Recommended</Badge> : null}
                </div>
                <Form.Check
                    id={inputId}
                    type="switch"
                    checked={Boolean(value)}
                    onChange={event => onChange(event.target.checked)}
                    disabled={disabled}
                    aria-describedby={field.helpText ? `${inputId}-help` : undefined}
                />
            </div>
        );
    }

    if (field.control === "integrationCard") {
        const enabled = Boolean(value);
        return (
            <button
                type="button"
                className={`product-integration-card ${enabled ? "is-selected" : ""}`}
                onClick={() => onChange(enabled ? "" : (field.defaultValue || field.key))}
                disabled={disabled}
                aria-pressed={enabled}
            >
                <span className="product-integration-icon" aria-hidden="true">↗</span>
                <span><strong>{field.label}</strong><small>{enabled ? "Included in setup" : "Not configured"}</small></span>
                <Badge bg={enabled ? "success" : "secondary"}>{enabled ? "Enabled" : "Optional"}</Badge>
            </button>
        );
    }

    const shared = {
        id: inputId,
        value: Array.isArray(value) ? value.join(field.control === "stringList" ? "\n" : ", ") : (value ?? ""),
        disabled,
        isInvalid: Boolean(error),
        placeholder: field.placeholder,
        min: field.minimum,
        max: field.maximum,
        "aria-describedby": field.helpText ? `${inputId}-help` : undefined,
        onChange: event => onChange(
            field.control === "multiSelect"
                ? event.target.value.split(",").map(item => item.trim()).filter(Boolean)
                : field.control === "stringList"
                    ? event.target.value.split(/\r?\n/)
                    : event.target.value
        )
    };

    return (
        <Form.Group className="product-setting-field">
            <Form.Label htmlFor={inputId}>{field.label}{field.required ? <span aria-hidden="true"> *</span> : null}</Form.Label>
            {field.description ? <Form.Text className="d-block mb-2">{field.description}</Form.Text> : null}
            {options.length ? (
                <Form.Select {...shared}>
                    {options.map(option => <option value={option.value} key={option.value}>{option.label}</option>)}
                </Form.Select>
            ) : ["textarea", "stringList"].includes(field.control) ? (
                <Form.Control {...shared} as="textarea" rows={field.control === "stringList" ? 5 : 4} />
            ) : (
                <Form.Control
                    {...shared}
                    type={field.control === "color"
                        ? "color"
                        : field.control === "date"
                            ? "date"
                            : field.control === "number"
                                ? "number"
                                : field.format === "url"
                                    ? "url"
                                    : "text"}
                />
            )}
            {field.helpText ? <Form.Text id={`${inputId}-help`}>{field.helpText}</Form.Text> : null}
            {error ? <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback> : null}
        </Form.Group>
    );
};

export const ProductSettingsWorkspace = ({
    uuid,
    canEdit,
    product,
    onManageSubscription,
    onRemoveProduct,
    onTagUpdated,
    lifecycle: loadLifecycle,
    saveLifecycleSettings: saveSettings,
    retryLifecycle: retry,
    repairLifecycle: repair,
    productManagement: loadManagement,
    searchProductManagers: searchManagers,
    addProductManager: addManager,
    inviteProductManager: inviteManager,
    removeProductManager: removeManager,
    changeProductManagerRole: changeManagerRole,
    resendProductManagerInvitation: resendInvitation,
    revokeProductManagerInvitation: revokeInvitation,
    tokenizeProduct: createProductToken,
    updateInternalTag: saveInternalTag
}) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [values, setValues] = useState({});
    const [activeSection, setActiveSection] = useState(0);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [message, setMessage] = useState(null);
    const [management, setManagement] = useState(null);
    const [managementOpen, setManagementOpen] = useState(false);
    const [managementLoading, setManagementLoading] = useState(true);
    const [managementError, setManagementError] = useState(null);
    const [loginPending, setLoginPending] = useState(false);
    const dirtyRef = useRef(false);

    const refresh = useCallback(async ({ quiet = false } = {}) => {
        if (!quiet) setLoading(true);
        const result = await loadLifecycle({ uuid });
        if (result?.success) {
            setData(result);
            if (!dirtyRef.current) setValues(result.settings?.values || {});
        } else if (!quiet) {
            setMessage({ variant: "danger", text: result?.error?.message || "Product setup could not be loaded." });
        }
        if (!quiet) setLoading(false);
        return result;
    }, [loadLifecycle, uuid]);

    const refreshManagement = useCallback(async () => {
        setManagementLoading(true);
        const result = await loadManagement({ uuid });
        if (result?.success) {
            setManagement(result);
            setManagementError(null);
        } else {
            setManagementError(result?.error?.message || "Product management access could not be loaded.");
        }
        setManagementLoading(false);
        return result;
    }, [loadManagement, uuid]);

    useEffect(() => {
        dirtyRef.current = dirty;
    }, [dirty]);

    useEffect(() => {
        refresh();
        refreshManagement();
        const timer = window.setInterval(() => refresh({ quiet: true }), 5000);
        return () => window.clearInterval(timer);
    }, [refresh, refreshManagement]);

    const sections = useMemo(() => data?.settingsDefinition?.sections || [], [data]);
    const section = sections[activeSection] || sections[0];
    const sectionValues = section ? (values[section.key] || {}) : {};
    const presentation = statusPresentation(data, saving);
    const removalPending = ["DeletionPending", "Deprovisioning", "DeprovisioningFailed", "Archived", "Deleted"].includes(data?.instance?.status);
    const setupComplete = data?.instance?.status === "Active" && data?.settings?.status === "Applied";
    const progress = sections.length ? Math.round(((activeSection + 1) / sections.length) * 100) : 0;
    const visibleFields = section ? section.fields.filter(field => matchesSettingRule(field.visibilityRule, sectionValues)) : [];
    const managementView = useMemo(() => {
        const source = management || { managers: [], invitations: [], summary: {} };
        const access = source.access || data?.access || {};
        return {
            ...source,
            access: {
                ...access,
                canManageSetup: Boolean(canEdit && (access.canManageSetup ?? true)),
                canManageAccess: Boolean(canEdit && access.canManageAccess),
                canManageSubscription: Boolean(canEdit && access.canManageSubscription),
                canDeleteProduct: Boolean(canEdit && access.canDeleteProduct),
                canEditInternalTag: Boolean(canEdit && access.canEditInternalTag),
            }
        };
    }, [canEdit, data?.access, management]);
    const effectiveCanEdit = managementView.access.canManageSetup;

    const changeValue = (key, value) => {
        if (!section) return;
        const nextSectionValues = { ...sectionValues, [key]: value };
        const baseline = data?.settings?.values?.[section.key] || {};
        const hasChanges = Object.keys(changedSettingValues(baseline, nextSectionValues)).length > 0;
        setValues(current => ({
            ...current,
            [section.key]: nextSectionValues
        }));
        setErrors(current => {
            const next = { ...current };
            delete next[`${section.key}.${key}`];
            return next;
        });
        dirtyRef.current = hasChanges;
        setDirty(hasChanges);
        setMessage(null);
    };

    const saveSection = async () => {
        if (!section || !effectiveCanEdit || saving || removalPending) return;
        const changes = changedSettingValues(data?.settings?.values?.[section.key] || {}, sectionValues);
        if (Object.keys(changes).length === 0) {
            dirtyRef.current = false;
            setDirty(false);
            return;
        }
        setSaving(true);
        setMessage(null);
        const result = await saveSettings({
            uuid,
            sectionKey: section.key,
            values: changes,
            expectedRevision: data?.settings?.revision || 0
        });
        setSaving(false);
        if (!result?.success) {
            setErrors(result?.error?.errors || {});
            setMessage({ variant: "danger", text: result?.error?.message || "This section could not be saved." });
            return;
        }
        dirtyRef.current = false;
        setDirty(false);
        setErrors({});
        setData(current => ({ ...current, settings: result.settings, operation: result.operation }));
        setValues(result.settings?.values || values);
        setMessage({ variant: "info", text: "Saved. Feradel is applying these settings to your product." });
    };

    const retryOperation = async () => {
        const operationId = data?.operation?.uuid;
        if (!operationId) return;
        const result = await retry({ uuid, operationId });
        setMessage(result?.success
            ? { variant: "info", text: "Retry scheduled. You can keep working while setup continues." }
            : { variant: "danger", text: result?.error?.message || "Retry could not be scheduled." });
        await refresh({ quiet: true });
    };

    const repairProduct = async () => {
        const result = await repair({ uuid });
        setMessage(result?.success
            ? { variant: "info", text: "Product setup has been queued." }
            : { variant: "danger", text: result?.error?.message || "Product setup could not be queued." });
        await refresh({ quiet: true });
    };

    const saveTag = async tag => {
        const result = await saveInternalTag({ uuid, tag });
        if (result?.success) onTagUpdated?.(result.tag);
        return result;
    };

    const loginToProduct = async () => {
        if (loginPending) return;
        setLoginPending(true);
        setMessage(null);
        const result = await createProductToken({ uuid });
        if (result?.success && result.url) {
            window.location.assign(result.url);
            return;
        }
        setLoginPending(false);
        setMessage({
            variant: "danger",
            text: result?.error?.message || "This product could not be opened."
        });
    };

    if (loading) {
        return <div className="product-settings-loading"><Spinner animation="border" size="sm" /><span>Loading product setup…</span></div>;
    }
    if (!data || !section) {
        return <Alert variant="warning">Guided settings are not available for this product yet.</Alert>;
    }

    return (
        <section className="product-settings-workspace" aria-labelledby="product-settings-title">
            <ProductIdentityHeader
                data={data}
                loginPending={loginPending}
                management={managementView}
                onLoginProduct={loginToProduct}
                onManageAccess={() => setManagementOpen(true)}
                onManageSubscription={onManageSubscription}
                onRemoveProduct={onRemoveProduct}
                presentation={presentation}
                product={product}
                removalPending={removalPending}
                setupComplete={setupComplete}
            />
            <ProductSummaryBand
                data={data}
                management={managementView}
                onManageAccess={() => setManagementOpen(true)}
                onSaveTag={saveTag}
                product={product}
            />

            {customDomainsUiEnabled && data.productKey === "matters" ? (
                <CustomDomainsSection
                    canEdit={effectiveCanEdit}
                    instanceId={data.instance?.uuid}
                    removalPending={removalPending}
                />
            ) : null}

            {removalPending ? (
                <Alert variant="warning" className="m-0 rounded-0">
                    Access is revoked. Product resources are being handled under the {data.instance?.retentionPolicyKey || "default"} retention policy.
                </Alert>
            ) : null}
            {message ? <Alert variant={message.variant} className="product-settings-message" role="status">{message.text}</Alert> : null}
            {data.operation?.status === "Failed" ? (
                <Alert variant="danger" className="product-settings-message">
                    <div>
                        <strong>Setup needs attention.</strong><br />Feradel could not finish the current setup step. Your saved settings were not changed.
                        <details className="product-technical-details">
                            <summary>View technical details</summary>
                            <span>{data.operation.errorCode || "product_setup_failed"}</span>
                            {data.operation.lastAttemptAt ? <span>Last attempted {new Date(data.operation.lastAttemptAt).toLocaleString()}</span> : null}
                            {data.operation.errorMessage ? <span>{data.operation.errorMessage}</span> : null}
                        </details>
                    </div>
                    {effectiveCanEdit ? <Button variant="outline-danger" size="sm" onClick={retryOperation}>Retry setup</Button> : null}
                </Alert>
            ) : null}

            <div className="product-settings-progress">
                <span>Step {activeSection + 1} of {sections.length}</span>
                <ProgressBar now={progress} visuallyHidden />
                <strong>{progress}%</strong>
            </div>

            <Row className="g-0">
                <Col lg="3" className="product-settings-nav-wrap">
                    <nav className="product-settings-nav" aria-label="Product settings sections">
                        {sections.map((item, index) => (
                            <button
                                type="button"
                                key={item.key}
                                className={index === activeSection ? "is-active" : ""}
                                onClick={() => {
                                    if (dirty && !window.confirm("Discard unsaved changes in this section?")) return;
                                    setActiveSection(index);
                                    dirtyRef.current = false;
                                    setDirty(false);
                                    setErrors({});
                                    setMessage(null);
                                }}
                            >
                                <span>{index + 1}</span>
                                <span><strong>{item.title}</strong><small>{index < activeSection ? "Reviewed" : index === activeSection ? "In progress" : "Not reviewed"}</small></span>
                            </button>
                        ))}
                    </nav>
                </Col>
                <Col lg="9">
                    <div className="product-settings-section">
                        <div className="product-settings-section-title">
                            <div><span>Step {activeSection + 1}</span><h3>{section.title}</h3><p>{section.description}</p></div>
                            {visibleFields.some(field => field.recommendedValue !== undefined) ? <Badge bg="light" text="primary">Recommended defaults included</Badge> : null}
                        </div>
                        <div className={`product-settings-fields layout-${section.layout || "form"}`}>
                            {visibleFields.map(field => (
                                <SettingControl
                                    key={field.key}
                                    field={field}
                                    value={sectionValues[field.key]}
                                    error={errors[`${section.key}.${field.key}`]}
                                    onChange={value => changeValue(field.key, value)}
                                    disabled={!effectiveCanEdit || removalPending || Boolean(field.readOnly) || Boolean(field.disabledRule && matchesSettingRule(field.disabledRule, sectionValues))}
                                />
                            ))}
                        </div>
                        <footer className="product-settings-actions">
                            <div>
                                {dirty ? <span className="product-settings-unsaved">Unsaved changes</span> : <span>Changes are synchronized after each section is saved.</span>}
                            </div>
                            <div>
                                {activeSection > 0 ? <Button variant="outline-secondary" onClick={() => setActiveSection(index => index - 1)} disabled={dirty || saving}>Previous</Button> : null}
                                {effectiveCanEdit ? <Button variant="primary" onClick={saveSection} disabled={!dirty || saving || removalPending}>{saving ? "Saving…" : `Save ${section.title}`}</Button> : null}
                                {activeSection < sections.length - 1 ? <Button variant="outline-primary" onClick={() => setActiveSection(index => index + 1)} disabled={dirty || saving}>Next section</Button> : null}
                            </div>
                        </footer>
                    </div>
                </Col>
            </Row>

            {!data.instance && effectiveCanEdit ? (
                <div className="product-settings-repair"><span>Product infrastructure has not been registered yet.</span><Button size="sm" onClick={repairProduct}>Start setup</Button></div>
            ) : null}
            <ProductManagementDrawer
                show={managementOpen}
                onHide={() => setManagementOpen(false)}
                loading={managementLoading}
                loadError={managementError}
                management={managementView}
                onRetry={refreshManagement}
                onRefresh={refreshManagement}
                onSearch={query => searchManagers({ uuid, query })}
                onAdd={manager => addManager({ uuid, ...manager })}
                onInvite={email => inviteManager({ uuid, email })}
                onChangeRole={manager => changeManagerRole({ uuid, personUuid: manager.personUuid })}
                onRemove={manager => removeManager({ uuid, personUuid: manager.personUuid })}
                onResend={invitation => resendInvitation({ uuid, invitationUuid: invitation.uuid })}
                onRevoke={invitation => revokeInvitation({ uuid, invitationUuid: invitation.uuid })}
            />
        </section>
    );
};

export default connect(null, {
    addProductManager,
    changeProductManagerRole,
    inviteProductManager,
    lifecycle,
    productManagement,
    removeProductManager,
    repairLifecycle,
    resendProductManagerInvitation,
    retryLifecycle,
    revokeProductManagerInvitation,
    saveLifecycleSettings,
    searchProductManagers,
    tokenizeProduct,
    updateInternalTag
})(ProductSettingsWorkspace);
